import type { TrainingAttributes } from '@/server/models'

import type { PrimaryTrainingType, SecondaryTrainingType } from '@/queries/constants'
import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import TrainingSubmissionQ from './TrainingSubmissionQ'

const TrainingQ = {
  ...makeQueryModelV2<TrainingAttributes>('training')([
    'id',
    'slug',
    'title',
    'excerpt',
    'description',
    'tagline',
    'primary_type',
    'secondary_type',
    'time_estimate_minutes',
    'link_training',
    'link_summary',
    'archived_at',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    TrainingSubmissions: {
      query: TrainingSubmissionQ,
      foreignKey: TrainingSubmissionQ.training_id,
      sourceKey: TrainingQ.id,
    },
  }),

  where: (
    {
      id,
      slug,
      primaryType,
      secondaryType,
      include = [],
      groupBy = null,
      order = null,
      includeArchived = false,
      paranoid = true,
    }: {
      id?: number | SQLFragment<number> | null
      slug?: string | null | SQLFragment<string>
      primaryType?: PrimaryTrainingType | null | SQLFragment<PrimaryTrainingType>
      secondaryType?: SecondaryTrainingType | null | SQLFragment<SecondaryTrainingType>
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      includeArchived?: boolean
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<TrainingAttributes> => SQL`
        FROM ${TrainingQ}
        ${includeAssociationsToSql(TrainingQ, include)}
        WHERE
            ${id != null ? SQL`${TrainingQ.id} = ${id} AND` : SQL``}
            ${slug != null ? SQL`${TrainingQ.slug} = ${slug} AND` : SQL``}
            ${primaryType != null ? SQL`${TrainingQ.primary_type} = ${primaryType} AND` : SQL``}
            ${
              secondaryType != null
                ? SQL`${TrainingQ.secondary_type} = ${secondaryType} AND`
                : SQL``
            }
            ${!includeArchived ? SQL`${TrainingQ.archived_at} IS NULL AND` : SQL``}
            ${paranoid ? SQL`${TrainingQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  getTrainingSubmissions: ({
    userId,
    slugs,
  }: {
    userId: number
    slugs: string[]
  }): SQLFragment<unknown> => SQL`
    FROM ${TrainingQ}
    LEFT JOIN ${TrainingSubmissionQ}
        ON (
            ${TrainingSubmissionQ.user_id} = ${userId}
            AND ${TrainingSubmissionQ.training_id} = ${TrainingQ.id}
            AND ${TrainingSubmissionQ.submitted_at} IS NOT NULL
            AND ${TrainingSubmissionQ.deleted_at} IS NULL
        )
    WHERE ${TrainingQ.deleted_at} IS NULL AND ${TrainingQ.slug} = ANY(${slugs})`,

  isPrimaryType(primaryType: PrimaryTrainingType): SQLFragment<boolean> {
    return SQL`${TrainingQ.primary_type} = ${primaryType}`
  },
}

export default TrainingQ
